import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Color, LIST_PAGES, Size, billTakesCredit, formatDateOnly, pageKindLineText, t, vendorCreditPills, type VendorCreditPageAnswer } from '@bms2/shared';
import { Button, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { FormFrame } from '../../components/forms/FormFrame';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { ApplyCreditModal } from './ApplyCreditModal';
import { BannerPills } from './BannerPills';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { RecordMessagesPanel } from '../mail/RecordMessagesPanel';
import { DocumentViewPage } from './DocumentViewPage';
import { type ScreenFact } from './PageFacts';
import { type TileRow } from './tileItems';
import { BILLS_PATH, VENDOR_CREDITS_PATH, useApi } from './api';
import { documentPdfPath } from './ViewPdfLink';
import styles from './DocumentPage.module.css';

/** What the Bills list answers for a row; the picker needs the number and the status that says whether the bill takes a credit. */
interface BillChoice {
  id: string;
  billNumber: string;
  status: string;
}

type OpenWindow = 'pick' | 'apply' | null;

/**
 * 🔒 The Vendor Credit's page (piece 24, Vendor credits; decision 7580, reversing
 * 1158): view mode draws the banner, the credit's facts, what it was applied to
 * and when, and the Files block, Internal Notes and History every document page
 * carries. A credit carries no table of lines (decision 0449), so there is none.
 *
 * 🔨 There is no edit mode: the raise window from the list is the way to create a
 * credit, and a mistake is voided. Apply credit is the page's one act. It asks
 * which of the vendor's bills takes the credit and then opens the Bill's own
 * Apply credit window on that bill with this credit chosen, so the refusals and
 * the amount rules are the Bill's and are written once.
 */
export function VendorCreditPage({ creditId, apiBaseUrl = '' }: { creditId: string; apiBaseUrl?: string }) {
  const resolveStatus = useStatusResolver();
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const page = useApi<VendorCreditPageAnswer & { history?: TileRow[] }>(`${VENDOR_CREDITS_PATH}/${creditId}`, apiBaseUrl);
  const [opened, setOpened] = useState<OpenWindow>(null);
  const [billId, setBillId] = useState('');
  // 🔨 A credit made from a mail message whose filing call failed arrives with the reason, so the page says the message is unfiled (T3498).
  const arrival = (useLocation().state as { notFiled?: string } | null)?.notFiled;
  const [notice, setNotice] = useState<string | null>(arrival ? `!${arrival}` : null);
  const vendorId = page.state.kind === 'ready' ? page.state.data.vendorCompanyId : null;
  // The picker reads the vendor's bills only when it opens, and offers the ones that take a credit (T740).
  const bills = useApi<{ rows: BillChoice[] }>(opened === 'pick' && vendorId ? `${BILLS_PATH}?vendorCompanyId=${encodeURIComponent(vendorId)}` : null, apiBaseUrl);

  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const takers = bills.state.kind === 'ready' ? bills.state.data.rows.filter((row) => billTakesCredit(row.status)) : [];
  const close = () => {
    setOpened(null);
    setBillId('');
  };

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(credit) => {
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={credit.currencyCode} />;
        const facts: ScreenFact[] = [
          { label: t('vendorCredit.fact.vendor'), value: credit.vendorName, route: `/companies/${credit.vendorCompanyId}` },
          { label: t('vendorCredit.fact.creditDate'), value: credit.creditDate },
          { label: t('vendorCredit.fact.amount'), value: moneyOf(credit.amountCents) },
          { label: t('vendorCredit.fact.applied'), value: moneyOf(credit.appliedCents) },
          { label: t('vendorCredit.fact.remaining'), value: moneyOf(credit.remainingCents) },
          { label: t('vendorCredit.fact.sourceBill'), value: credit.sourceBillNumber, route: credit.sourceBillId ? `/bills/${credit.sourceBillId}` : null },
          { label: t('vendorCredit.fact.reason'), value: credit.reason },
        ];
        const footer = credit.mayApply ? (
          <Button size={Size.Medium} color={Color.Navy} text={[t('vendorCredit.apply.act')]} actions={[() => setOpened('pick')]} />
        ) : null;
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(credit);
        const notesPanel = <DocumentNotes recordType="VendorCredit" recordId={credit.id} recordLabel={credit.creditNumber} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // 🔒 The files attached to this credit, where the vendor's own credit document goes (decisions 0457, 7581).
        const filesPanel = <DocumentFilesSection recordType="VendorCredit" recordId={credit.id} writeRefusal={credit.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} />;
        // One History panel, placed in the Details view and handed to the PDF view's rail as its History tab (T1070; decision 1662).
        const historyPanel = <DocumentHistoryPanel recordType="VendorCredit" recordId={credit.id} rows={credit.history ?? []} timeZone={zone} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            history={historyPanel}
            documentType="VendorCredit"
            icon="accounting"
            title={credit.creditNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'VendorCredit' })}
            information={facts}
            status={<BannerPills pills={vendorCreditPills(credit, resolveStatus)} attribute="data-vendor-credit-pills" />}
            footer={footer}
            footerAttributes={{ 'data-vendor-credit-actions': credit.mayApply ? 'apply' : 'none' }}
            pageAttributes={{ 'data-vendor-credit': credit.id, 'data-vendor-credit-status': credit.status }}
            layout={credit.printLayout ?? null}
            sheet={credit.printSheet ?? null}
            writeRefusal={credit.writeRefusal ?? null}
            ribbon={credit.status}
            pdfPath={documentPdfPath(VENDOR_CREDITS_PATH, credit.id)}
            listRoute={LIST_PAGES.VendorCredit.route}
            apiBaseUrl={apiBaseUrl}
            notices={notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
            details={
              <>
                {/* 🔒 What the credit was applied to and when, a dissociated application listed and marked (decision 7580). */}
                <section className={styles.block} data-vendor-credit-applications={credit.applications.length}>
                  <h2 className={styles.heading}>{t('vendorCredit.applied.title')}</h2>
                  {credit.applications.length === 0 ? <p className={styles.muted} data-vendor-credit-applications-none="true">{t('vendorCredit.applied.none')}</p> : null}
                  <DetailList look="separator">
                    {credit.applications.map((row) => (
                      <DetailRow
                        key={row.id}
                        data-vendor-credit-application={row.id}
                        data-vendor-credit-application-closed={row.closedAt ? 'true' : 'false'}
                        title={<Link to={`/bills/${row.billId}`} data-vendor-credit-application-bill={row.billId}>{row.billNumber ?? row.billId}</Link>}
                        detail={row.closedAt ? t('vendorCredit.applied.dissociated') : null}
                        columns={[{ value: formatDateOnly(row.appliedOn, dateFormat) }]}
                      >
                        <span className={styles.rowAmount}>{moneyOf(row.amountCents)}</span>
                      </DetailRow>
                    ))}
                  </DetailList>
                </section>
                {filesPanel}
                {/* 🔒 The mail filed against this credit from the accounting mailbox (decision 10042; T3425). */}
                <RecordMessagesPanel recordType="VendorCredit" recordId={credit.id} timeZone={zone} apiBaseUrl={apiBaseUrl} />
                {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
                {notesPanel}
                {historyPanel}
              </>
            }
          >
            {opened === 'pick' ? (
              /* 🔨 A window with a field draws through the Form frame (decision 3060); it is unmounted when it closes, so the next opening starts clean. */
              <FormFrame
                host="window"
                open
                title={t('vendorCredit.apply.pickTitle')}
                onCancel={close}
                dataForm="vendor-credit-pick-bill"
                typed={billId !== ''}
                act={{ label: t('vendorCredit.apply.next'), onAct: () => setOpened('apply'), disabled: billId === '', disabledReason: t('vendorCredit.apply.pickHint') }}
              >
                <p className={modalStyles.fieldLabel}>{t('vendorCredit.apply.pickHint')}</p>
                {bills.state.kind === 'ready' && takers.length === 0 ? <p className={modalStyles.fieldLabel} data-vendor-credit-no-bills="true">{t('vendorCredit.apply.none')}</p> : null}
                {takers.length > 0 ? (
                  <Select
                    size={Size.Medium}
                    label={t('vendorCredit.apply.bill')}
                    value={billId}
                    onChange={setBillId}
                    options={takers.map((row) => ({ value: row.id, label: row.billNumber }))}
                    dataAttribute={{ 'data-vendor-credit-bill': 'true' }}
                    required
                  />
                ) : null}
              </FormFrame>
            ) : null}
            {opened === 'apply' && billId !== '' ? (
              <ApplyCreditModal
                key={`vendor-credit-apply-${credit.id}-${billId}`}
                source={{ billId }}
                chosen={{ kind: 'VendorCredit', id: credit.id }}
                apiBaseUrl={apiBaseUrl}
                onCancel={close}
                onDone={(message) => {
                  close();
                  setNotice(message);
                  page.reload();
                }}
              />
            ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}
